﻿import type { ArcEntity, CircleEntity, DrawingPoint3D, EllipseEntity, PointEntity } from "@dxf-ai-editor/drawing-model";
import { BASE_BYTES_PER_ENTITY, createBaseColumns, growBaseColumns, readBase, writeBase, type BaseColumns } from "./compactStoreBase";
import { type SharedStringTable } from "./sharedStringTable";

const POINT_FLOATS = 4;
// CIRCLE: x, y, radius, thickness, centerZ (DXF group 30 — S-ARC-CIRCLE-CENTER-Z).
const CIRCLE_FLOATS = 5;
// ARC: x, y, radius, startAngle, endAngle, thickness, centerZ (group 30).
const ARC_FLOATS = 7;
// ELLIPSE: center.x/y, majorAxis.x/y, ratio, startParam, endParam, thickness,
// center.z, majorAxis.z (DXF 30/31 — SL-15). The two z slots use the NO_Z NaN
// sentinel so the optional-z distinction (z absent vs z === 0) round-trips.
const ELLIPSE_FLOATS = 10;

// NaN sentinel for "no explicit z" so the compact round-trip preserves the
// optional-z distinction (center.z absent vs center.z === 0). Float32Array
// cannot hold `undefined`; a stored NaN means "z was not present on the model
// entity" and `*FromCompact` omits the z field, mirroring the bridge's
// optional-z contract (drawingPointOptionalZSchema).
const NO_Z = Number.NaN;

export interface CompactPointStore { count: number; base: BaseColumns; floats: Float32Array; }
export function createCompactPointStore(initialCapacity = 1024): CompactPointStore {
  const cap = Math.max(1, initialCapacity);
  return { count: 0, base: createBaseColumns(cap), floats: new Float32Array(cap * POINT_FLOATS) };
}
function ensurePointCap(s: CompactPointStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const nf = new Float32Array(c * POINT_FLOATS); nf.set(s.floats); s.floats = nf;
}
export function appendPoint(s: CompactPointStore, t: SharedStringTable, e: PointEntity): number {
  const i = s.count; ensurePointCap(s, i + 1); s.count++; setPoint(s, t, i, e); return i;
}
export function setPoint(s: CompactPointStore, t: SharedStringTable, i: number, e: PointEntity): void {
  const f = i * POINT_FLOATS;
  s.floats[f + 0] = e.location.x; s.floats[f + 1] = e.location.y;
  s.floats[f + 2] = e.rotation; s.floats[f + 3] = e.thickness;
  writeBase(s.base, t, i, e);
}
export function pointFromCompact(s: CompactPointStore, t: SharedStringTable, i: number): PointEntity {
  if (i < 0 || i >= s.count) throw new RangeError(`pointFromCompact: index ${i} out of range (count=${s.count})`);
  const f = i * POINT_FLOATS;
  return { ...readBase(s.base, t, i), type: "Point", location: { x: s.floats[f + 0]!, y: s.floats[f + 1]! }, rotation: s.floats[f + 2]!, thickness: s.floats[f + 3]! };
}
export function estimatePointBytesPerEntity(): number { return POINT_FLOATS * 4 + BASE_BYTES_PER_ENTITY; }

export interface CompactCircleStore { count: number; base: BaseColumns; floats: Float32Array; }
export function createCompactCircleStore(initialCapacity = 1024): CompactCircleStore {
  const cap = Math.max(1, initialCapacity);
  return { count: 0, base: createBaseColumns(cap), floats: new Float32Array(cap * CIRCLE_FLOATS) };
}
function ensureCircleCap(s: CompactCircleStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const nf = new Float32Array(c * CIRCLE_FLOATS); nf.set(s.floats); s.floats = nf;
}
export function appendCircle(s: CompactCircleStore, t: SharedStringTable, e: CircleEntity): number {
  const i = s.count; ensureCircleCap(s, i + 1); s.count++; setCircle(s, t, i, e); return i;
}
export function setCircle(s: CompactCircleStore, t: SharedStringTable, i: number, e: CircleEntity): void {
  const f = i * CIRCLE_FLOATS;
  s.floats[f + 0] = e.center.x; s.floats[f + 1] = e.center.y;
  s.floats[f + 2] = e.radius; s.floats[f + 3] = e.thickness;
  s.floats[f + 4] = e.center.z ?? NO_Z;
  writeBase(s.base, t, i, e);
}
export function circleFromCompact(s: CompactCircleStore, t: SharedStringTable, i: number): CircleEntity {
  if (i < 0 || i >= s.count) throw new RangeError(`circleFromCompact: index ${i} out of range (count=${s.count})`);
  const f = i * CIRCLE_FLOATS;
  // Defensive: treat an undefined slot (short/old buffer that predates the
  // centerZ slot, or any path that didn't write it) the SAME as the NaN
  // sentinel → z-absent. `Number.isNaN(undefined)` is `false`, so reading the
  // slot via `!` + isNaN-only would embed `undefined` into center.z.
  const z = s.floats[f + 4];
  const center = (z === undefined || Number.isNaN(z)) ? { x: s.floats[f + 0]!, y: s.floats[f + 1]! } : { x: s.floats[f + 0]!, y: s.floats[f + 1]!, z };
  return { ...readBase(s.base, t, i), type: "Circle", center, radius: s.floats[f + 2]!, thickness: s.floats[f + 3]! };
}
export function estimateCircleBytesPerEntity(): number { return CIRCLE_FLOATS * 4 + BASE_BYTES_PER_ENTITY; }

export interface CompactArcStore { count: number; base: BaseColumns; floats: Float32Array; }
export function createCompactArcStore(initialCapacity = 1024): CompactArcStore {
  const cap = Math.max(1, initialCapacity);
  return { count: 0, base: createBaseColumns(cap), floats: new Float32Array(cap * ARC_FLOATS) };
}
function ensureArcCap(s: CompactArcStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const nf = new Float32Array(c * ARC_FLOATS); nf.set(s.floats); s.floats = nf;
}
export function appendArc(s: CompactArcStore, t: SharedStringTable, e: ArcEntity): number {
  const i = s.count; ensureArcCap(s, i + 1); s.count++; setArc(s, t, i, e); return i;
}
export function setArc(s: CompactArcStore, t: SharedStringTable, i: number, e: ArcEntity): void {
  const f = i * ARC_FLOATS;
  s.floats[f + 0] = e.center.x; s.floats[f + 1] = e.center.y;
  s.floats[f + 2] = e.radius;
  s.floats[f + 3] = e.startAngle; s.floats[f + 4] = e.endAngle;
  s.floats[f + 5] = e.thickness;
  s.floats[f + 6] = e.center.z ?? NO_Z;
  writeBase(s.base, t, i, e);
}
export function arcFromCompact(s: CompactArcStore, t: SharedStringTable, i: number): ArcEntity {
  if (i < 0 || i >= s.count) throw new RangeError(`arcFromCompact: index ${i} out of range (count=${s.count})`);
  const f = i * ARC_FLOATS;
  // Defensive: treat an undefined slot (short/old buffer that predates the
  // centerZ slot, or any path that didn't write it) the SAME as the NaN
  // sentinel → z-absent. `Number.isNaN(undefined)` is `false`, so reading the
  // slot via `!` + isNaN-only would embed `undefined` into center.z.
  const z = s.floats[f + 6];
  const center = (z === undefined || Number.isNaN(z)) ? { x: s.floats[f + 0]!, y: s.floats[f + 1]! } : { x: s.floats[f + 0]!, y: s.floats[f + 1]!, z };
  return { ...readBase(s.base, t, i), type: "Arc", center, radius: s.floats[f + 2]!, startAngle: s.floats[f + 3]!, endAngle: s.floats[f + 4]!, thickness: s.floats[f + 5]! };
}
export function estimateArcBytesPerEntity(): number { return ARC_FLOATS * 4 + BASE_BYTES_PER_ENTITY; }

export interface CompactEllipseStore { count: number; base: BaseColumns; floats: Float32Array; extrusions: Map<number, DrawingPoint3D>; }
const DEFAULT_EXTRUSION: DrawingPoint3D = { x: 0, y: 0, z: 1 };
function isDefaultExtrusion(e: DrawingPoint3D | undefined): boolean { if (!e) return true; return e.x === 0 && e.y === 0 && e.z === 1; }
export function createCompactEllipseStore(initialCapacity = 1024): CompactEllipseStore {
  const cap = Math.max(1, initialCapacity);
  return { count: 0, base: createBaseColumns(cap), floats: new Float32Array(cap * ELLIPSE_FLOATS), extrusions: new Map() };
}
function ensureEllipseCap(s: CompactEllipseStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const nf = new Float32Array(c * ELLIPSE_FLOATS); nf.set(s.floats); s.floats = nf;
}
export function appendEllipse(s: CompactEllipseStore, t: SharedStringTable, e: EllipseEntity): number {
  const i = s.count; ensureEllipseCap(s, i + 1); s.count++; setEllipse(s, t, i, e); return i;
}
export function setEllipse(s: CompactEllipseStore, t: SharedStringTable, i: number, e: EllipseEntity): void {
  const f = i * ELLIPSE_FLOATS;
  s.floats[f + 0] = e.center.x; s.floats[f + 1] = e.center.y;
  s.floats[f + 2] = e.majorAxisEndpoint.x; s.floats[f + 3] = e.majorAxisEndpoint.y;
  s.floats[f + 4] = e.ratio;
  s.floats[f + 5] = e.startParam; s.floats[f + 6] = e.endParam;
  s.floats[f + 7] = e.thickness;
  // SL-15 — center.z (DXF 30) + majorAxis.z (31); NO_Z when absent (optional-z).
  s.floats[f + 8] = e.center.z ?? NO_Z;
  s.floats[f + 9] = e.majorAxisEndpoint.z ?? NO_Z;
  writeBase(s.base, t, i, e);
  if (isDefaultExtrusion(e.extrusion)) s.extrusions.delete(i);
  else s.extrusions.set(i, { ...e.extrusion });
}
export function ellipseFromCompact(s: CompactEllipseStore, t: SharedStringTable, i: number): EllipseEntity {
  if (i < 0 || i >= s.count) throw new RangeError(`ellipseFromCompact: index ${i} out of range (count=${s.count})`);
  const f = i * ELLIPSE_FLOATS;
  const extrusion = s.extrusions.get(i) ?? { ...DEFAULT_EXTRUSION };
  // SL-15 — treat an undefined slot (old/short buffer predating the z slots) or
  // the NaN sentinel as z-absent, exactly like Circle/Arc centerZ.
  const cz = s.floats[f + 8];
  const mz = s.floats[f + 9];
  const center = (cz === undefined || Number.isNaN(cz)) ? { x: s.floats[f + 0]!, y: s.floats[f + 1]! } : { x: s.floats[f + 0]!, y: s.floats[f + 1]!, z: cz };
  const majorAxisEndpoint = (mz === undefined || Number.isNaN(mz)) ? { x: s.floats[f + 2]!, y: s.floats[f + 3]! } : { x: s.floats[f + 2]!, y: s.floats[f + 3]!, z: mz };
  return { ...readBase(s.base, t, i), type: "Ellipse", center, majorAxisEndpoint, ratio: s.floats[f + 4]!, startParam: s.floats[f + 5]!, endParam: s.floats[f + 6]!, thickness: s.floats[f + 7]!, extrusion };
}
export function estimateEllipseBytesPerEntity(): number { return ELLIPSE_FLOATS * 4 + BASE_BYTES_PER_ENTITY; }
